import { randomUUID as uuidv4 } from 'expo-crypto';
import { usePathname } from 'expo-router';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { ICartEntry } from 'features/cart/types';
import {
  mapChildrenPriceToMenuSelectionsPrice,
  mapMenuSelectionsToICartEntry,
  mapMenuSelectionsToMinimalCartEntry,
  mapSelectedProductToMenuSelections,
} from 'features/cart/utils/minimal-cart';
import { useIntl } from 'react-intl';

import { toaster } from 'components/toaster/toaster';
import { useCart } from 'hooks/use-cart';
import { useAppliedReward } from 'pages/loyalty/loyalty-rewards/use-applied-reward';
import { useFavoritesContext } from 'state/favorites';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { LoyaltyAppliedOffer } from 'state/loyalty/types';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { mapImageResourceToSanityImage } from 'state/unified-menu/utils';

import { SlotsSelections } from './types';
import { useProductWizardFlow } from './use-product-wizard-flow';

export const useAddToCart = () => {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const { indexedMenu, indexedOffers, indexedRewards } = useUnifiedMenuContext();
  const { addItemToMinimalCart, hydrateMinimalCart } = useMinimalCart();
  const { posServiceMode } = useMenuQueryOptions();
  const { cartIdEditing, rewardBenefitId, rewardId, offerId } = useProductWizardFlow();
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const appliedReward = useAppliedReward();
  const { removeFromCart } = useCart();
  const { editFavoriteOneMenuAdapter, tempFavorite: currentFavorite } = useFavoritesContext();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const enableMinimalCartUi = useFlag(LaunchDarklyFlag.ENABLE_MINIMAL_CART_UI);

  const { formatMessage } = useIntl();
  /**
   * @deprecated
   * this function is used to add items to the legacy cart, it supports some of the hydration logic
   * for the legacy cart.
   * We should migrate this function to use the minimal cart.
   * This function will be removed in the future.
   */
  const addToLegacyCart = (
    selectedProduct: ILocalizedMenuEntry,
    quantity: number,
    slotsSelections: SlotsSelections
  ) => {
    const menuSelections = mapSelectedProductToMenuSelections(
      selectedProduct,
      quantity,
      slotsSelections
    );

    // If there is a favorite active in the context, add to the favorite instead of the cart
    if (currentFavorite) {
      const [mappedEntry] = mapMenuSelectionsToMinimalCartEntry(menuSelections, {
        menu: indexedMenu,
        indexedOffers,
        posServiceMode,
        url: pathname,
        offerId,
      });

      return editFavoriteOneMenuAdapter({
        minimalCartEntries: [mappedEntry],
      });
    }

    const rewardBenefitSelection = menuSelections[0];

    const [mappedEntry] = mapMenuSelectionsToMinimalCartEntry(
      rewardId ? [rewardBenefitSelection] : menuSelections,
      {
        menu: indexedMenu,
        indexedOffers,
        posServiceMode,
        url: pathname,
        rewardId,
        offerId,
      }
    );

    const isRewardEntry = !cartIdEditing && rewardBenefitId;

    const minimalCartEntry = {
      ...mappedEntry,
      cartId: cartIdEditing || uuidv4(),
      ...(cartIdEditing && { isEdit: true }),
      ...(isRewardEntry && { rewardBenefitId, rewardId }),
    };

    // Remove already applied reward if applying a new one
    if (enableRewardUnstacking && isRewardEntry && appliedReward) {
      removeFromCart({ cartId: appliedReward.cartId });
    }

    if (cartIdEditing) {
      hydrateMinimalCart([minimalCartEntry]);
    } else {
      addItemToMinimalCart(minimalCartEntry);
    }

    if (selectedLoyaltyOffer?._id === minimalCartEntry.id) {
      if (!selectedLoyaltyOffer.isStackable) {
        appliedOffers.forEach(offer => {
          if (!offer.isStackable && !!offer.cartId) {
            removeFromCart({ cartId: offer.cartId });
          }
        });
      }
      dispatch(
        actions.loyalty.applyOffer({
          id: selectedLoyaltyOffer.loyaltyEngineId,
          type: (selectedLoyaltyOffer as LoyaltyAppliedOffer).type,
          cartId: minimalCartEntry.cartId,
          isStackable: selectedLoyaltyOffer.isStackable,
          cmsId: selectedLoyaltyOffer._id,
        })
      );
      dispatch(actions.loyalty.setSelectedOffer(null));
    }

    return toaster.addToCartToast(
      `${formatMessage(
        { id: cartIdEditing ? 'updateCartSuccess' : 'addToCartSuccess' },
        { itemName: minimalCartEntry.name }
      )}`,
      rewardId
        ? mapImageResourceToSanityImage(
            selectedProduct.image?.resource ?? '',
            selectedProduct.image?.altText?.locale ?? ''
          )
        : minimalCartEntry.image
    );
  };

  /**
   * This function is used to add items to the minimal cart, it follows one menu logic and add items using a hierarchy
   */
  const addToCart = (
    selectedProduct: ILocalizedMenuEntry,
    quantity: number,
    slotsSelections: SlotsSelections
  ) => {
    let menuSelections = mapSelectedProductToMenuSelections(
      selectedProduct,
      quantity,
      slotsSelections
    );

    if (rewardId) {
      menuSelections = [
        {
          id: rewardId,
          quantity, // Check the stacking logic here?
          menuSelections,
        },
      ];
    }

    const [mappedEntry]: ICartEntry[] = mapMenuSelectionsToICartEntry(menuSelections, {
      menu: indexedMenu,
      posServiceMode,
      url: pathname,
      indexedOffers,
      indexedRewards,
    });

    // The mapped entry has price at children level, we have to add the price to the menu selections tree
    const menuSelectionsWithPrice = mapChildrenPriceToMenuSelectionsPrice(mappedEntry);
    // mappedEntry.menuSelections is readonly, so create a new object with updated menuSelections
    const mappedEntryWithPrices = {
      ...mappedEntry,
      menuSelections: menuSelectionsWithPrice,
    };

    // If there is a favorite active in the context, add to the favorite instead of the cart
    if (currentFavorite) {
      return editFavoriteOneMenuAdapter({
        minimalCartEntries: [mappedEntryWithPrices],
      });
    }

    const minimalCartEntry = {
      ...mappedEntryWithPrices,
      ...(cartIdEditing && { isEdit: true }),
      cartId: cartIdEditing || uuidv4(),
    };

    addItemToMinimalCart(minimalCartEntry);

    return toaster.addToCartToast(
      `${formatMessage(
        { id: cartIdEditing ? 'updateCartSuccess' : 'addToCartSuccess' },
        { itemName: minimalCartEntry.name }
      )}`,
      rewardId
        ? mapImageResourceToSanityImage(
            selectedProduct.image?.resource ?? '',
            selectedProduct.image?.altText?.locale ?? ''
          )
        : minimalCartEntry.image
    );
  };

  return enableMinimalCartUi ? addToCart : addToLegacyCart;
};
